import { FC } from "react";
import { observer } from "mobx-react-lite";
import { CalendarConnectionConfigurationListView } from "./calendar-connection-configuration-list.view";
import type {
  CalendarConnectionConfigurationListProps,
  CalendarConnectionStatus,
} from "./calendar-connection-configuration-list.types";
import type { CalendarConnectionConfigurationListItemProps } from "./calendar-connection-configuration-list-item/calendar-connection-configuration-list-item.types";
import { resolveCalendarConnectionUuid } from "@/modules/uuid/sync-models/resolveCalendarConnectionUuid";
import { resolveConnectedCalendarUuid } from "@/modules/uuid/sync-models/resolveConnectedCalendarUuid";
import { compareUuids } from "@/modules/uuid/compareUuids";
import { getCalendarSyncStatus } from "@/domains/calendar/getCalendarSyncStatus";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";

export const CalendarConnectionConfigurationList: FC<CalendarConnectionConfigurationListProps> =
  observer(
    ({
      connection,
      subjectId,
      connectionStatus = "CONNECTED",
      accountMenu,
      settingsCalendars,
      onToggleCalendar,
      onRetryCalendar,
      isTogglingCalendar = false,
      togglingCalendar = null,
      onConnectionReconnect,
      refreshingCalendarIds,
    }) => {
      const email = connection.associatedEmailAddress;
      const providerKind = connection.providerKind;
      const provider =
        providerKind === "GOOGLE_CALENDAR" ? ("google" as const) : ("outlook" as const);

      // Determine effective connection status based on sync model and passed status
      const effectiveConnectionStatus: CalendarConnectionStatus =
        connection.isConnected === false ? "DISCONNECTED" : connectionStatus;

      const isDisconnected =
        effectiveConnectionStatus === "DISCONNECTED" ||
        effectiveConnectionStatus === "RECONNECTING";

      const calendarConnectionId = resolveCalendarConnectionUuid({
        spaceAccountId: connection.associatedSpaceAccountId,
        subjectId,
        providerKind: providerKind ?? "",
      });

      const handleCalendarRefresh = (
        connectedCalendarId: string,
        syncModelCalendar: (typeof connection.allCalendars)[number] | undefined
      ) => {
        const previousSyncStatus = getCalendarSyncStatus({
          webhookStatus: syncModelCalendar?.webhookStatus ?? null,
          syncStartedAt: syncModelCalendar?.syncStartedAt ?? null,
          syncEndedAt: syncModelCalendar?.syncEndedAt ?? null,
          lastSyncEndedWithError: syncModelCalendar?.lastSyncEndedWithError ?? null,
          isConnected: connection.isConnected ?? true,
        });
        trackEvent(TrackedEvent.CalendarRefreshManual, {
          provider,
          connected_calendar_id: connectedCalendarId,
          calendar_connection_id: calendarConnectionId,
          previous_sync_status: previousSyncStatus ?? "SYNCED",
        });
        onRetryCalendar?.(connectedCalendarId);
      };

      const calendarItems: CalendarConnectionConfigurationListItemProps[] = settingsCalendars.map(
        settingsCalendar => {
          const expectedConnectedCalendarId = resolveConnectedCalendarUuid({
            calendarConnectionId,
            externalCalendarId: settingsCalendar.id,
          });
          const syncModelCalendar = connection.allCalendars.find(cal =>
            compareUuids(
              cal.modelData.associated_connected_calendar_id,
              expectedConnectedCalendarId
            )
          );
          const connectedCalendarId = syncModelCalendar?.associatedConnectedCalendarId ?? null;

          const isTogglingThis = togglingCalendar?.id === settingsCalendar.id;
          const togglingStatus = isTogglingThis
            ? togglingCalendar.targetEnabled
              ? "CONNECTING"
              : "DISCONNECTING"
            : null;

          return {
            id: settingsCalendar.id,
            label: settingsCalendar.summary || "Untitled Calendar",
            isPrimary: settingsCalendar.isPrimary,
            isCalendarEnabled: settingsCalendar.isEnabled,
            isDisabled: isTogglingCalendar || isDisconnected,
            togglingStatus,
            backgroundColor: settingsCalendar.backgroundColor ?? syncModelCalendar?.color,
            webhookStatus: syncModelCalendar?.webhookStatus ?? null,
            syncStartedAt: syncModelCalendar?.syncStartedAt ?? null,
            syncEndedAt: syncModelCalendar?.syncEndedAt ?? null,
            lastSyncEndedWithError: syncModelCalendar?.lastSyncEndedWithError ?? null,
            isConnected: connection.isConnected ?? true,
            onToggle: (isEnabled: boolean) => onToggleCalendar?.(settingsCalendar.id, isEnabled),
            onRetry:
              onRetryCalendar && connectedCalendarId
                ? () => handleCalendarRefresh(connectedCalendarId, syncModelCalendar)
                : undefined,
            isRefreshing: connectedCalendarId
              ? refreshingCalendarIds?.has(connectedCalendarId)
              : false,
            isConnectionDisconnected: isDisconnected,
          };
        }
      );

      return (
        <CalendarConnectionConfigurationListView
          email={email}
          provider={provider}
          calendars={calendarItems}
          connectionStatus={effectiveConnectionStatus}
          onConnectionReconnect={onConnectionReconnect}
          accountMenu={accountMenu}
        />
      );
    }
  );
